import isEqual from 'lodash/isEqual'
import React, { useState } from 'react'

import { SEARCH_APPEARS_ON_FILTER_FIELD } from '@disco/constants/search'
import {
  selectSearchPage,
  setFilters,
} from '@disco/state-management/reducers/search-page'

import type { FilterOperator, FilterState } from 'types/filter-page'

import { Flex, Popover } from 'components/ds'
import { ColumnHeaderFilterIcon } from 'components/search/search-columns/column-header-filter-icon'
import { APPEARS_ON_FILTER_DEFINITION } from 'components/search/search-filters/filter-definitions'
import FilterFooter from 'components/search/search-filters/filter-badge/filter-footer'
import NumberForm from 'components/search/search-filters/filter-forms/number'
import { canCommitFilterValue } from 'components/search/search-filters/utils'

import { useDispatch, useSelector } from 'hooks/use-redux'

const findAppearsOnFilter = (filters: FilterState[]) =>
  filters.find(f => f.definition.field === SEARCH_APPEARS_ON_FILTER_FIELD)

/**
 * Filter control in the "Appears on" column header. Mirrors the Source column's
 * icon-in-header pattern, but edits the shared `filters` slice via the same
 * NumberForm the filters dropdown uses, so header and dropdown stay in sync.
 */
export const AppearsOnColumnFilter = () => {
  const dispatch = useDispatch()
  const { filters } = useSelector(selectSearchPage)
  const existing = findAppearsOnFilter(filters)

  const [isOpen, setIsOpen] = useState(false)
  const [tempValue, setTempValue] = useState(existing?.value)
  const [tempOperator, setTempOperator] = useState(existing?.operator)
  const [isComponentValid, setIsComponentValid] = useState(true)

  const isFiltered =
    existing?.value !== undefined && existing?.operator !== undefined

  const canCommit =
    canCommitFilterValue('number', tempValue, tempOperator) && isComponentValid
  const isFilterUpdated = !isEqual(tempValue, existing?.value) && canCommit
  const showFooter = Boolean(tempValue) || Boolean(existing?.value)

  const handleChange = (value: any, operator?: FilterOperator) => {
    setTempValue(value)
    setTempOperator(operator)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      // Re-seed staging state from what's committed each time we open.
      setTempValue(existing?.value)
      setTempOperator(existing?.operator)
      setIsComponentValid(true)
    }
  }

  const handleApply = () => {
    if (!canCommit) return

    const updated: FilterState = {
      ...(existing ?? { definition: APPEARS_ON_FILTER_DEFINITION }),
      value: tempValue,
      operator: tempOperator,
    }
    const next = existing
      ? filters.map(f =>
          f.definition.field === SEARCH_APPEARS_ON_FILTER_FIELD ? updated : f,
        )
      : [...filters, updated]
    dispatch(setFilters(next))
    setIsOpen(false)
  }

  const handleClear = () => {
    dispatch(
      setFilters(
        filters.filter(
          f => f.definition.field !== SEARCH_APPEARS_ON_FILTER_FIELD,
        ),
      ),
    )
    setTempValue(undefined)
    setTempOperator(undefined)
    setIsOpen(false)
  }

  return (
    <Flex onClick={e => e.stopPropagation()}>
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
        <Popover.Trigger>
          <ColumnHeaderFilterIcon
            isFiltered={isFiltered}
            aria-label="Filter by number of playlists"
          />
        </Popover.Trigger>
        <Popover.Content minWidth="240px" maxWidth="380px" maxHeight="400px">
          <Flex direction="column" gap="4">
            <Flex width="100%" asChild>
              <NumberForm
                label="Appears on"
                field={SEARCH_APPEARS_ON_FILTER_FIELD}
                value={tempValue}
                operator={tempOperator}
                onChange={handleChange}
                onPress={handleApply}
                onValidityChange={setIsComponentValid}
              />
            </Flex>
            {showFooter && (
              <FilterFooter
                onClearSelection={handleClear}
                onApplySelection={handleApply}
                isFilterUpdated={isFilterUpdated}
              />
            )}
          </Flex>
        </Popover.Content>
      </Popover.Root>
    </Flex>
  )
}
